import { Link, useLocation } from "react-router-dom";
import { Phone, MessageSquare, Menu, X } from "lucide-react";
import { useState } from "react";
import { Dialog, DialogContent, DialogTrigger } from "@/components/ui/dialog";
import { PHONE, PHONE_TEL } from "@/lib/constants";

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [photoOpen, setPhotoOpen] = useState(false);
  const { pathname } = useLocation();

  const nav = [
    { to: "/", label: "Home" },
    { to: "/shingles", label: "Shingles" },
    { to: "/#inspect", label: "Free Inspection" },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-40 backdrop-blur-xl bg-slate-950/80 border-b border-slate-800"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between py-4">
        <div className="flex items-center gap-3">
          <Dialog open={photoOpen} onOpenChange={setPhotoOpen}>
            <DialogTrigger asChild>
              <button
                type="button"
                data-testid="logo-photo-btn"
                aria-label="View JD's photo"
                className="h-11 w-11 rounded-full overflow-hidden border-2 border-red-600 ring-2 ring-slate-950 hover:ring-red-600 transition-all shrink-0 cursor-pointer"
              >
                <img
                  src="/jd-photo.png"
                  alt="JD Dean — Storm Damage Specialist"
                  className="w-full h-full object-cover"
                />
              </button>
            </DialogTrigger>
            <DialogContent
              data-testid="photo-modal"
              className="max-w-2xl w-[92vw] p-0 bg-slate-950 border-red-600 border-2 rounded-none overflow-hidden"
            >
              <img
                src="/jd-photo-full.png"
                alt="JD Dean — Storm Damage Specialist"
                className="w-full h-auto block"
              />
              <div className="px-6 py-5 bg-slate-950 border-t border-slate-800">
                <div className="font-display font-black uppercase text-xl text-white">JD Dean</div>
                <div className="text-xs tracking-[0.2em] uppercase text-red-600 mt-1">
                  Weather and Storm Damage Expert
                </div>
                <div className="text-xs tracking-[0.2em] uppercase text-red-600">
                  Certified Roofing Specialist
                </div>
                <a
                  href={`tel:${PHONE_TEL}`}
                  data-testid="modal-phone"
                  className="mt-3 inline-flex items-center gap-2 font-display font-bold text-white text-lg hover:text-red-500 transition-colors"
                >
                  <Phone size={16} className="text-red-600" /> {PHONE}
                </a>
              </div>
            </DialogContent>
          </Dialog>

          <Link to="/" data-testid="logo-link" className="leading-tight group">
            <div className="font-display font-black uppercase text-sm tracking-widest text-white group-hover:text-red-500 transition-colors">
              Storm Damage Pros
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-red-600/90">
              JD Dean · Roof Inspections
            </div>
          </Link>
        </div>

        <nav className="hidden md:flex items-center gap-8">
          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={`link-underline text-sm uppercase tracking-widest font-bold ${
                pathname === n.to ? "text-red-600" : "text-slate-200 hover:text-white"
              }`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-3">
          <a
            data-testid="header-text-btn"
            href={`sms:${PHONE_TEL}`}
            className="bg-transparent border-2 border-slate-700 text-white font-bold uppercase tracking-wide hover:border-red-600 hover:text-red-600 px-4 py-2 inline-flex items-center gap-2 text-xs transition-colors"
          >
            <MessageSquare size={14} /> Text
          </a>
          <a
            data-testid="header-call-btn"
            href={`tel:${PHONE_TEL}`}
            className="bg-red-600 text-white font-bold uppercase tracking-wide hover:bg-red-500 px-4 py-2 inline-flex items-center gap-2 text-xs transition-colors"
          >
            <Phone size={14} /> {PHONE}
          </a>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-slate-800 bg-slate-950">
          <div className="px-6 py-6 flex flex-col gap-5">
            {nav.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                data-testid={`mnav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="text-base uppercase tracking-widest font-bold text-white"
              >
                {n.label}
              </Link>
            ))}
            <div className="flex gap-3 pt-2">
              <a
                href={`sms:${PHONE_TEL}`}
                className="flex-1 bg-transparent border-2 border-slate-700 text-white font-bold uppercase px-4 py-3 inline-flex items-center justify-center gap-2 text-sm"
              >
                <MessageSquare size={16} /> Text
              </a>
              <a
                href={`tel:${PHONE_TEL}`}
                className="flex-1 bg-red-600 text-white font-bold uppercase px-4 py-3 inline-flex items-center justify-center gap-2 text-sm"
              >
                <Phone size={16} /> Call
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};
